/*
 * @forgerock/ping-javascript-sdk
 *
 * index.ts
 *
 * Copyright (c) 2020 - 2025 Ping Identity Corporation. All rights reserved.
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { reduceToObject, reduceToString } from '@forgerock/sdk-utilities';
import { logger as loggerFn } from '@forgerock/sdk-logger';
import { browserProps, configurableCategories, delay, devicePlatforms, displayProps, fontNames, hardwareProps, platformProps, } from './defaults.js';
/**
 * @class JourneyDevice - Collects user device metadata.
 *
 * Example:
 *
 * ```js
 * // Instantiate new device object (w/optional config, if needed)
 * const device = new Device({
 *   // optional configuration
 * });
 *
 * // Call getProfile with required argument obj of boolean properties
 * // of location and metadata
 * const profile = await device.getProfile({
 *   location: isLocationRequired,
 *   metadata: isMetadataRequired,
 * });
 * ```
 */
export class Device {
    config;
    logger;
    prefix;
    constructor(configOptions, logLevel = 'info', prefix = 'forgerock') {
        this.logger = loggerFn({ level: logLevel });
        this.prefix = prefix;
        this.config = {
            fontNames,
            devicePlatforms,
            displayProps,
            browserProps,
            hardwareProps,
            platformProps,
        };
        if (configOptions) {
            Object.keys(configOptions).forEach((key) => {
                if (!configurableCategories.includes(key)) {
                    throw new Error('Device profile configuration category does not exist.');
                }
                this.config[key] = configOptions[key];
            });
        }
    }
    getBrowserMeta() {
        if (typeof navigator === 'undefined') {
            this.logger.warn('Cannot collect browser metadata. navigator is not defined.');
            return {};
        }
        return reduceToObject(this.config.browserProps, navigator);
    }
    getBrowserPluginsNames() {
        if (!(typeof navigator !== 'undefined' && navigator.plugins)) {
            this.logger.warn('Cannot collect browser plugin information. navigator.plugins is not defined.');
            return '';
        }
        return reduceToString(Object.keys(navigator.plugins), navigator.plugins);
    }
    getDeviceName() {
        if (typeof navigator === 'undefined') {
            this.logger.warn('Cannot collect device name. navigator is not defined.');
            return '';
        }
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        switch (true) {
            case this.config.devicePlatforms.mac.includes(platform):
                return 'Mac (Browser)';
            case this.config.devicePlatforms.ios.includes(platform):
                return `${platform} (Browser)`;
            case this.config.devicePlatforms.windows.includes(platform):
                return 'Windows (Browser)';
            case /Android/.test(platform) || /Android/.test(userAgent):
                return 'Android (Browser)';
            case /CrOS/.test(userAgent) || /Chromebook/.test(userAgent):
                return 'Chrome OS (Browser)';
            case /Linux/.test(platform):
                return 'Linux (Browser)';
            default:
                return `${platform || 'Unknown'} (Browser)`;
        }
    }
    getDisplayMeta() {
        if (typeof screen === 'undefined') {
            this.logger.warn('Cannot collect screen information. screen is not defined.');
            return {};
        }
        return reduceToObject(this.config.displayProps, screen);
    }
    getHardwareMeta() {
        if (typeof navigator === 'undefined') {
            this.logger.warn('Cannot collect OS metadata. Navigator is not defined.');
            return {};
        }
        return reduceToObject(this.config.hardwareProps, navigator);
    }
    getIdentifier() {
        const storageKey = `${this.prefix}-DeviceID`;
        if (!(typeof globalThis.crypto !== 'undefined' && globalThis.crypto.getRandomValues)) {
            this.logger.warn('Cannot generate profile ID. Crypto and/or getRandomValues is not supported.');
            return '';
        }
        if (!localStorage) {
            this.logger.warn('Cannot store profile ID. localStorage is not supported.');
            return '';
        }
        let id = localStorage.getItem(storageKey);
        if (!id) {
            // generate ID, 3 sections of random numbers: "714524572-2799534390-3707617532"
            id = globalThis.crypto.getRandomValues(new Uint32Array(3)).join('-');
            localStorage.setItem(storageKey, id);
        }
        return id;
    }
    getInstalledFonts() {
        if (typeof document === 'undefined') {
            this.logger.warn('Cannot collect font data. Global document object is undefined.');
            return '';
        }
        const canvas = document.createElement('canvas');
        if (!canvas) {
            this.logger.warn('Cannot collect font data. Browser does not support canvas element');
            return '';
        }
        const context = canvas.getContext && canvas.getContext('2d');
        if (!context) {
            this.logger.warn('Cannot collect font data. Browser does not support 2d canvas context');
            return '';
        }
        const text = 'abcdefghi0123456789';
        context.font = '72px Comic Sans';
        const baseWidth = context.measureText(text).width;
        const installedFonts = this.config.fontNames.reduce((prev, curr) => {
            context.font = `72px ${curr}, Comic Sans`;
            const newWidth = context.measureText(text).width;
            if (newWidth !== baseWidth) {
                prev = `${prev}${curr};`;
            }
            return prev;
        }, '');
        return installedFonts;
    }
    async getLocationCoordinates() {
        if (!(typeof navigator !== 'undefined' && navigator.geolocation)) {
            this.logger.warn('Cannot collect geolocation information. navigator.geolocation is not defined.');
            return Promise.resolve({});
        }
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition((position) => resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            }), () => {
                this.logger.warn('Cannot collect geolocation information. Geolocation API error.');
                resolve({});
            }, {
                enableHighAccuracy: true,
                timeout: delay,
                maximumAge: 0,
            });
        });
    }
    getOSMeta() {
        if (typeof navigator === 'undefined') {
            this.logger.warn('Cannot collect OS metadata. navigator is not defined.');
            return {};
        }
        return reduceToObject(this.config.platformProps, navigator);
    }
    getTimezoneOffset() {
        try {
            return new Date().getTimezoneOffset();
        }
        catch {
            this.logger.warn('Cannot collect timezone information. getTimezoneOffset is not defined.');
            return null;
        }
    }
    async getProfile({ location, metadata }) {
        const profile = {
            identifier: this.getIdentifier(),
        };
        if (metadata) {
            profile.metadata = {
                hardware: {
                    ...this.getHardwareMeta(),
                    display: this.getDisplayMeta(),
                },
                browser: {
                    ...this.getBrowserMeta(),
                    plugins: this.getBrowserPluginsNames(),
                },
                platform: {
                    ...this.getOSMeta(),
                    deviceName: this.getDeviceName(),
                    fonts: this.getInstalledFonts(),
                    timezone: this.getTimezoneOffset(),
                },
            };
        }
        if (location) {
            profile.location = await this.getLocationCoordinates();
        }
        return profile;
    }
}
